import { useQuery } from '@apollo/client'
import {
  BIG_ITEMS_PER_PAGE,
  INITIAL_PAGE_INDEX,
  SMALL_ITEMS_PER_PAGE,
} from 'constants/pagination'
import { useItemsPerPage } from 'hooks/useItemsPerPage'
import { usePagination } from 'hooks/usePagination'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { firebaseCollections, firebaseDatabase } from 'services/firebase'
import {
  LatestStoresQuery,
  MarketplaceController,
} from '../types/Marketplace.type'
import { GET_LATEST_STORES } from '../utils/Marketplace.graphql'

const useLatestStoresController = (props: MarketplaceController) => {
  const { initialPageIndex = INITIAL_PAGE_INDEX, filter } = props

  const router = useRouter()

  const [page, setCurrentPage] = useState(initialPageIndex)
  const [totalItems, setTotalItems] = useState(0)
  const [data, setData] = useState<
    | {
        headerImage: any
        name: string
        id: string
      }[]
    | []
  >([])

  const { itemsPerPage } = useItemsPerPage({
    smallScreenItems: SMALL_ITEMS_PER_PAGE,
    bigScreenItems: BIG_ITEMS_PER_PAGE,
  })

  const getStoreImage = async (contractName: string) => {
    const snapshot = await firebaseDatabase
      .collection(firebaseCollections.STORE)
      .where('contract', '==', contractName)
      .get()

    const doc = snapshot.docs[0]

    if (doc && doc.exists) {
      const data = doc.data()

      return data.headerImage === '' ? null : data.headerImage
    }

    return null
  }

  const { loading, error } = useQuery<LatestStoresQuery>(GET_LATEST_STORES, {
    onCompleted: async (_data) => {
      setTotalItems(_data.store.length)

      // go through each store and get the header image
      const stores = await Promise.all(
        _data.store.map(async (_) => {
          const headerImage = await getStoreImage(_.id)
          return {
            ..._,
            headerImage: headerImage,
          }
        })
      )
      setData(stores)
    },
  })

  const { setPage } = usePagination({
    totalItems: totalItems,
    initialPageSize: itemsPerPage,
    initialPage: INITIAL_PAGE_INDEX,
  })

  const handlePageChange = () => {
    if (loading) return
    window.scrollTo({ top: 0, behavior: 'smooth' })
    setPage(page)
    router.push(`/market?tab=stores&orderby=${filter}&page=${initialPageIndex}`)
  }

  useEffect(() => {
    handlePageChange()
  }, [page])

  return {
    data,
    loading: loading || !data,
    error: error,
    page,
    itemsPerPage,
    totalItems,
    setCurrentPage,
  }
}

export { useLatestStoresController }
